import { isNil } from '@flex-design-system/fx-utils/common';
import { useState, useCallback } from 'react';
import { Key } from 'w3c-keys';
import { stopEvent } from '../utils/event.mjs';

const NAVIGATION_KEY = [Key.ArrowDown, Key.ArrowUp];
const INITIAL_INDEX = -1;
function isNavigationKey(eventKey) {
  return NAVIGATION_KEY.includes(eventKey);
}
function findNonDisabledIndex(listRef, { startingIndex = INITIAL_INDEX, decrement = false } = {}) {
  const list = getPresentListItems(listRef);
  let index = decrement ? Math.max(1, startingIndex) : Math.min(startingIndex, list.length - 1);
  do {
    index = index + (decrement ? -1 : 1);
  } while (index >= 0 && index < list.length && (list[index]?.current?.hasAttribute("disabled") || list[index]?.current?.hasAttribute("hidden") || list[index]?.current?.getAttribute("aria-disabled") === "true"));
  return index === -1 ? 0 : Math.min(index, list.length - 1);
}
function getMinIndex(listRef) {
  return findNonDisabledIndex(listRef);
}
function getMaxIndex(listRef) {
  return findNonDisabledIndex(listRef, {
    decrement: true,
    startingIndex: getPresentListItems(listRef).length
  });
}
function getPresentListItems(elementList) {
  return elementList.filter(
    (item) => !isNil(item)
  );
}
function isOrientationToEndKey(key) {
  return key === Key.ArrowDown;
}
function useListNavigation({
  getListRef,
  activeIndex: activeIndexFromProps,
  onNavigate,
  onKeyDown,
  loop,
  focusClassName,
  scrollLogicalPosition = "nearest"
}) {
  const [activeIndex, setActiveIndex] = useState(
    activeIndexFromProps ?? INITIAL_INDEX
  );
  const [activedescendantId, setActivedescendantId] = useState("");
  const resetActiveIndex = useCallback(() => {
    setActiveIndex(INITIAL_INDEX);
  }, []);
  const setFocusStyle = useCallback(
    (listRef, nextIndex) => {
      const presentListItems = getPresentListItems(listRef);
      presentListItems.forEach((listItem, index) => {
        if (index === nextIndex) {
          listItem.current?.classList.add(focusClassName);
        } else {
          listItem.current?.classList.remove(focusClassName);
        }
      });
    },
    [focusClassName]
  );
  const setFocusA11y = useCallback((listRef, nextIndex) => {
    setActivedescendantId(getPresentListItems(listRef)[nextIndex]?.current?.id);
  }, []);
  const getNextIndex = useCallback(
    (orientation) => {
      const elementListRef = getListRef().map((item) => item.ref);
      const minIndex = Math.max(getMinIndex(elementListRef), 0);
      const maxIndex = Math.min(
        getMaxIndex(elementListRef),
        elementListRef.length - 1
      );
      let nextIndex = activeIndex;
      if (orientation === "end") {
        if (loop) {
          nextIndex = activeIndex === maxIndex ? minIndex : findNonDisabledIndex(elementListRef, {
            startingIndex: activeIndex
          });
        } else {
          nextIndex = Math.min(
            maxIndex,
            findNonDisabledIndex(elementListRef, {
              startingIndex: activeIndex
            })
          );
        }
      }
      if (orientation === "start") {
        if (loop) {
          nextIndex = activeIndex === minIndex ? maxIndex : findNonDisabledIndex(elementListRef, {
            startingIndex: activeIndex,
            decrement: true
          });
        } else {
          nextIndex = Math.max(
            minIndex,
            findNonDisabledIndex(elementListRef, {
              startingIndex: activeIndex,
              decrement: true
            })
          );
        }
      }
      return nextIndex;
    },
    [activeIndex, getListRef, loop]
  );
  const handleActiveIndex = useCallback(
    (targetIndex) => {
      const elementListRef = getListRef().map((item) => item.ref);
      const presentListItems = getPresentListItems(elementListRef);
      onNavigate?.(targetIndex);
      setFocusStyle(elementListRef, targetIndex);
      presentListItems[targetIndex]?.current?.scrollIntoView({
        block: scrollLogicalPosition
      });
      setActiveIndex(targetIndex);
      setFocusA11y(elementListRef, targetIndex);
    },
    [getListRef, onNavigate, scrollLogicalPosition, setFocusA11y, setFocusStyle]
  );
  const handleKeyDown = useCallback(
    (event) => {
      if (isNavigationKey(event.key)) {
        stopEvent(event, { prevent: true });
        const eventKey = event.key;
        const nextIndex = getNextIndex(
          isOrientationToEndKey(eventKey) ? "end" : "start"
        );
        handleActiveIndex(nextIndex);
      }
      onKeyDown?.(event);
    },
    [getNextIndex, handleActiveIndex, onKeyDown]
  );
  return [
    activeIndex,
    {
      onKeyDown: handleKeyDown,
      setActiveIndex: handleActiveIndex,
      resetActiveIndex,
      "aria-activedescendant": activedescendantId
    }
  ];
}

export { useListNavigation as default };
//# sourceMappingURL=useListNavigation.mjs.map
